import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot, Slottable } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import { cn } from './cn.js';
/**
 * Sidebar family — the dashboard nav rail every reference shares: a brand
 * header, grouped nav sections, an active item that fills to a signal-orange
 * pill, and a footer pinned to the bottom (profile / logout).
 *
 * Composition (not baked into one component) so app code owns the item list,
 * routing, and which item is active:
 *   <Sidebar>
 *     <SidebarHeader>…brand…</SidebarHeader>
 *     <SidebarContent>
 *       <SidebarSection label="Operations">
 *         <SidebarItem icon={<Home/>} active>Home</SidebarItem>
 *         <SidebarItem icon={<Briefcase/>} badge={3} asChild><a href="…">Jobs</a></SidebarItem>
 *       </SidebarSection>
 *     </SidebarContent>
 *     <SidebarFooter>…profile…</SidebarFooter>
 *   </Sidebar>
 */
export const Sidebar = forwardRef(({ className, ...props }, ref) => (_jsx("aside", { ref: ref, className: cn('flex h-full w-60 shrink-0 flex-col border-r border-sidebar-border ' +
        'bg-sidebar text-sidebar-foreground', className), ...props })));
Sidebar.displayName = 'Sidebar';
export const SidebarHeader = forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn('flex items-center gap-2.5 p-4', className), ...props })));
SidebarHeader.displayName = 'SidebarHeader';
export const SidebarContent = forwardRef(({ className, ...props }, ref) => (_jsx("nav", { ref: ref, className: cn('flex flex-1 flex-col gap-4 overflow-y-auto px-3 py-2', className), ...props })));
SidebarContent.displayName = 'SidebarContent';
export const SidebarSection = forwardRef(({ className, label, children, ...props }, ref) => (_jsxs("div", { ref: ref, className: cn('flex flex-col gap-0.5', className), ...props, children: [label != null && (_jsx("span", { className: "px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground", children: label })), children] })));
SidebarSection.displayName = 'SidebarSection';
/**
 * A nav row. Active = the filled signal-orange pill (the one accent moment in
 * the rail); inactive rows stay muted and lift to a neutral hover. 44px tall for
 * the touch target.
 */
export const SidebarItem = forwardRef(({ className, icon, active = false, badge, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    return (_jsxs(Comp, { ref: ref, "aria-current": active ? 'page' : undefined, className: cn('group flex h-11 items-center gap-3 rounded-md px-3 text-sm font-medium ' +
            'transition-colors duration-[var(--dur-quick)] ease-[var(--ease-out)] ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 ' +
            'motion-reduce:transition-none [&_svg]:size-[18px] [&_svg]:shrink-0', active
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground', className), ...props, children: [icon && (_jsx("span", { className: "contents", "aria-hidden": "true", children: icon })), asChild ? (_jsx(Slottable, { children: children })) : (_jsx("span", { className: "flex-1 truncate", children: children })), badge != null && (_jsx("span", { className: cn('inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-2xs font-semibold tabular-nums', active ? 'bg-primary-foreground/20 text-sidebar-accent-foreground' : 'bg-primary text-primary-foreground'), children: badge }))] }));
});
SidebarItem.displayName = 'SidebarItem';
export const SidebarFooter = forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn('mt-auto flex items-center gap-3 border-t border-sidebar-border p-3', className), ...props })));
SidebarFooter.displayName = 'SidebarFooter';
